import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";

interface InlineImageUploadProps {
  images: (File | { type: string; url: string })[];
  onChange: (images: File[]) => void;
}

export const InlineImageUpload = ({
  images,
  onChange,
}: InlineImageUploadProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.composeMessage",
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const image = images[0];

  useEffect(() => {
    if (image) {
      if (image instanceof File) {
        const url = URL.createObjectURL(image);
        setImageUrl(url);
        return () => URL.revokeObjectURL(url);
      } else {
        setImageUrl(image.url);
      }
    } else {
      setImageUrl(null);
    }
  }, [image]);

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const newImage = files[0];
      if (newImage && newImage.type.startsWith("image/")) {
        onChange([newImage]);
      }
    },
    [onChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileSelect(e.target.files);
    },
    [handleFileSelect],
  );

  const handleRemoveImage = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onChange([]);
    },
    [onChange],
  );

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileInputChange}
        data-testid="file-input"
      />

      {image ? (
        <div
          data-testid="image-preview"
          className="group relative overflow-hidden rounded-md border"
        >
          <img
            src={imageUrl || undefined}
            alt={t("uploadedImageAlt", { number: 1 })}
            className="h-auto w-full object-cover"
          />
          <Button
            size="sm"
            variant="ghost"
            type="button"
            onClick={handleRemoveImage}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/0 bg-black/60 px-2.5 py-0.5 opacity-0 shadow-sm backdrop-blur-md transition-opacity group-hover:opacity-100 hover:bg-black/70"
            aria-label={t("removeImage")}
            data-testid="remove-image-button"
          >
            <span className="text-xs font-bold text-white">
              {t("removeImage")}
            </span>
          </Button>
        </div>
      ) : (
        <div
          data-testid="image-upload-area"
          className={cn(
            "flex min-h-[100px] cursor-pointer items-center justify-center rounded-lg border-1 border-dashed p-3 text-center transition-colors",
            isDragging
              ? "border-accent bg-accent"
              : "border-border hover:border-accent",
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex flex-col items-center space-y-1">
            <Upload className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              {t("uploadImage")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("supportedFormats")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
